import React from 'react'
import { BodyContainer, Cell, Footer, Header, Row, Table } from 'wed-components-beta'
import { Data, SelectedSubmission, SubmissionsAuditStatistics } from './types'
import { Alert, Checkbox, Pagination, Skeleton, Tooltip } from '@mui/material'
import { formatAssignmentName, formatAttemptTime, formatCellStyle, formatDate } from '../IssueCredits/CreditsAuditingDetails'
import { AuditWarningFalse, AuditWarningTrue } from '../IssueCredits/IssueCreditsTable'
import { CancelPresentation } from '@mui/icons-material'

type Props = {
	data: Data | null
	isLoading: boolean
	error: boolean
	page: number
	setPage: React.Dispatch<React.SetStateAction<number>>
	selectedRows: Map<number, SelectedSubmission>,
	handleSelectRow: (row: SubmissionsAuditStatistics) => void,
	handleSelectAll: () => void
}

export function formatStringLength(value: string) {
	if (value.length > 45) return value.slice(0, 45) + '...'
	return value
}

export default function SubmissionsLogsTable({
	data,
	isLoading,
	error,
	page,
	setPage,
	selectedRows,
	handleSelectRow,
	handleSelectAll
}: Props) {

	if (isLoading || !data) return <Skeleton variant="rectangular" width={'100%'} height={'75vh'} />

	if (error) return <Alert severity="error">Error loading submissions logs</Alert>

	const columns = [
		{ title: 'checkbox', width: 0.5 },
		{ title: 'ID', width: 0.5 },
		{ title: 'Student', width: 3 },
		{ title: 'Section', width: 2.5 },
		{ title: 'Assignment', width: 3 },
		{ title: 'Submitted At', width: 1.25 },
		{ title: 'Attempt', width: 0.75 },
		{ title: 'Page Blurred', width: 1 },
		{ title: 'Time on Attempt', width: 1.25 },
		{ title: 'Score', width: 0.75 },
		{ title: 'Auditable', width: 0.75 },
	] as const

	const meta = data.meta
	const submissions = data.data

	return <>

		<Table>
			<BodyContainer id={'body-container'}>
				<Header style={{ zIndex: 1 }}>
					<Cell width={columns[0].width}>
						<Checkbox indeterminate={selectedRows.size > 0 && selectedRows.size !== submissions.length} checked={selectedRows.size === submissions.length && submissions.length > 0} disabled={submissions.length === 0} onClick={handleSelectAll} />
					</Cell>
					<Cell width={columns[1].width}>
						ID
					</Cell>
					<Cell width={columns[2].width}>
						Student
					</Cell>
					<Cell width={columns[3].width}>
						Section
					</Cell>
					<Cell width={columns[4].width}>
						Assignment
					</Cell>
					<Cell width={columns[5].width}>
						Submitted At
					</Cell>
					<Cell width={columns[6].width}>
						Attempt
					</Cell>
					<Cell width={columns[7].width}>
						Page Blurred
					</Cell>
					<Cell width={columns[8].width}>
						Time on Attempt
					</Cell>
					<Cell width={columns[9].width}>
						Score
					</Cell>
					<Cell width={columns[10].width}>
						Auditable
					</Cell>
				</Header>
				{
					submissions.length ?
						submissions.map((submission, index) => {
						const fontColor = submission.canceled_state ? 'rgba(255, 255, 255, 0.5)' : '#fff'

						return <Row key={index} rowHeight={48}>
							<Cell width={columns[0].width}>
								<Checkbox disabled={Boolean(submission.canceled_state)} checked={selectedRows.has(submission.fk_id_submission) && !Boolean(submission.canceled_state)} onClick={() => handleSelectRow(submission)} />
							</Cell>
							<Cell width={columns[1].width} style={{ color: fontColor }} >
								{submission.fk_id_enrollment}
							</Cell>
							<Cell width={columns[2].width} flexGrow style={{ color: fontColor, height: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'start' }} >
								{formatStringLength(submission.student_full_name)}
								{
									Boolean(submission.canceled_state) === true?
									<Tooltip title='Canceled submission' sx={{ paddingLeft: '0.5rem' }} >
										<CancelPresentation />
									</Tooltip>
									:
									<></>
								}
							</Cell>
							<Cell width={columns[3].width} style={{ color: fontColor }}>
								<p>{submission.class_tag}</p>
								<p>{submission.section_name}</p>
							</Cell>

							<Cell width={columns[4].width}>
								{formatAssignmentName(submission.assignment_name, submission.assignment_link, 45)}
							</Cell>
							<Cell width={columns[5].width} style={{ color: fontColor }} >
								{formatDate(submission.submitted_at)}
							</Cell>
							<Cell width={columns[6].width} style={{ textAlign: 'center', color: fontColor }}>
								{submission.attempt}
							</Cell>
							<Cell width={columns[7].width} style={formatCellStyle(submission.exceed_page_blur_limit)}>
								{submission.page_blurred}
							</Cell>
							<Cell width={columns[8].width} style={formatCellStyle(submission.under_minimum_time_on_attempt)}>
								{formatAttemptTime(submission.time_on_attempt)}
							</Cell>
							<Cell width={columns[9].width} style={formatCellStyle(submission.suspicious_score_fast_attempt)}>
								{submission.score}
							</Cell>
							<Cell width={columns[10].width}>
								{submission.auditable ? <AuditWarningTrue /> : <AuditWarningFalse />}
							</Cell>
						</Row>
						})
						:
						<Row>
							<Cell flexGrow>
								No submissions to show.
							</Cell>
						</Row>
				}
				<Footer>
					<Cell width={columns[2].width}>
						<p>
							Showing: {((meta.current_page - 1) * meta.per_page) + 1} - {Math.min(meta.current_page * meta.per_page, meta.total)} out of {meta.total}
							{selectedRows.size > 0 && ` | Selected: ${selectedRows.size}`}
						</p>
					</Cell>
					<Cell flexGrow width={5}>
						<Pagination disabled={selectedRows.size > 0} page={page} onChange={(e, newPage) => setPage(newPage)} count={meta.last_page} sx={{ marginX: 'auto' }} />
						{
							selectedRows.size > 0
							&&
							<small style={{ right: '1rem', position: 'absolute' }}>*Can't change pages when selecting rows</small>
						}
					</Cell>
				</Footer>
			</BodyContainer>
		</Table>

	</>
}
